import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { serializeArgsWithoutToken } from "../serializeQueryArgs";
import { apiFunction } from "../utils";

/**
 * Fabrika (Okkalı Factory) API — storage roles.
 * Server: /api/factory/storageRoles (D-022 panel bölümü).
 *
 * Okuma ağırlıklı: genel bakış, parça kataloğu/envanteri, üretim kayıtları,
 * bölgeler & tabletler. Yazma yalnız bölge yönetimi ve geç iptal.
 */
const BASE = "/api/factory/storageRoles";

const buildQs = (params) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "" && value !== "all") {
      qs.append(key, value);
    }
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
};

const authGet = (token, url) => ({
  url,
  method: "GET",
  headers: { Authorization: `Bearer ${token}` },
});

/**
 * Multipart resim yüklemesi — apiFunction JSON gövde kurar, burada FormData lazım.
 * Content-Type ELLE verilmez: boundary'yi tarayıcının üretmesi gerekir.
 */
const imageUpload = (token, url, file) => {
  const body = new FormData();
  body.append("image", file);
  return {
    url,
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body,
  };
};

export const factoryApi = createApi({
  reducerPath: "factory",
  tagTypes: ["FactoryOverview", "FactoryPart", "FactoryPartUnit", "FactoryMachine", "FactoryZone", "FactoryOperation", "PanelActivity", "FactoryDocument"],
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  keepUnusedDataFor: 60,
  endpoints(builder) {
    return {
      // ── Genel bakış ────────────────────────────────────────────────────
      getFactoryOverview: builder.query({
        query: ({ token }) => authGet(token, `${BASE}/overview`),
        transformResponse: (r) => r?.data || null,
        providesTags: [{ type: "FactoryOverview", id: "MAIN" }],
      }),

      getFactoryOperations: builder.query({
        query: ({ token, ...params }) =>
          authGet(token, `${BASE}/operations${buildQs(params)}`),
        transformResponse: (r) => r?.data || { items: [], totalCount: 0 },
        providesTags: [{ type: "FactoryOperation", id: "LIST" }],
      }),

      cancelFactoryOperationLate: builder.mutation({
        query: ({ token, id, reason }) =>
          apiFunction({ token, reason }, `${BASE}/operations/${id}/cancel-late`, "POST"),
        invalidatesTags: [
          { type: "FactoryOperation", id: "LIST" },
          { type: "FactoryOverview", id: "MAIN" },
        ],
      }),

      // ── Parçalar ───────────────────────────────────────────────────────
      getFactoryParts: builder.query({
        query: ({ token }) => authGet(token, `${BASE}/parts`),
        transformResponse: (r) => r?.data || { parts: [], totalUnitCount: 0 },
        providesTags: [{ type: "FactoryPart", id: "LIST" }],
      }),

      getFactoryPartUnits: builder.query({
        query: ({ token, ...params }) =>
          authGet(token, `${BASE}/part-units${buildQs(params)}`),
        transformResponse: (r) => r?.data || { items: [], totalCount: 0 },
        providesTags: [{ type: "FactoryPartUnit", id: "LIST" }],
      }),

      getFactoryPartUnitDetail: builder.query({
        query: ({ token, id }) => authGet(token, `${BASE}/part-units/${id}`),
        transformResponse: (r) => r?.data || null,
        providesTags: (_res, _err, arg) => [{ type: "FactoryPartUnit", id: arg.id }],
      }),

      deleteFactoryPartUnit: builder.mutation({
        query: ({ token, id }) =>
          apiFunction({ token }, `${BASE}/part-units/${id}`, "DELETE"),
        invalidatesTags: [
          { type: "FactoryPartUnit", id: "LIST" },
          // Parça tipi kartlarındaki durum sayıları (unitCounts) da değişir
          { type: "FactoryPart", id: "LIST" },
        ],
      }),

      // ── Makineler ──────────────────────────────────────────────────────
      getFactoryMachines: builder.query({
        query: ({ token, ...params }) =>
          authGet(token, `${BASE}/machines${buildQs(params)}`),
        transformResponse: (r) => r?.data || { items: [], totalCount: 0 },
        providesTags: [{ type: "FactoryMachine", id: "LIST" }],
      }),

      getFactoryBuilds: builder.query({
        query: ({ token }) => authGet(token, `${BASE}/builds`),
        transformResponse: (r) => r?.data?.items || [],
        providesTags: [{ type: "FactoryMachine", id: "BUILDS" }],
      }),

      // ── Katalog (eylem/kahve/makine tipi — read-only) ──────────────────
      getFactoryCatalog: builder.query({
        query: ({ token }) => authGet(token, `${BASE}/catalog`),
        transformResponse: (r) => r?.data || { actions: [], coffeeTypes: [], machineTypes: [] },
        providesTags: [{ type: "FactoryPart", id: "CATALOG" }],
      }),

      // ── Katalog yönetimi (D-058 — factory.catalog.manage) ──────────────
      getFactoryFullCatalog: builder.query({
        query: ({ token }) => authGet(token, `${BASE}/catalog/full`),
        transformResponse: (r) => r?.data || { parts: [], machineTypes: [] },
        providesTags: [{ type: "FactoryPart", id: "FULL" }],
      }),

      // Etiketler: kategori (parçanın cinsi) + ürün ailesi — D-061
      createFactoryLabel: builder.mutation({
        query: ({ token, ...body }) =>
          apiFunction({ token, ...body }, `${BASE}/catalog/labels`, "POST"),
        invalidatesTags: [{ type: "FactoryPart", id: "FULL" }],
      }),

      updateFactoryLabel: builder.mutation({
        query: ({ token, id, ...body }) =>
          apiFunction({ token, ...body }, `${BASE}/catalog/labels/${id}`, "PATCH"),
        invalidatesTags: [{ type: "FactoryPart", id: "FULL" }],
      }),

      deleteFactoryLabel: builder.mutation({
        query: ({ token, id }) =>
          apiFunction({ token }, `${BASE}/catalog/labels/${id}`, "DELETE"),
        invalidatesTags: [{ type: "FactoryPart", id: "FULL" }],
      }),

      createFactoryPart: builder.mutation({
        query: ({ token, ...body }) =>
          apiFunction({ token, ...body }, `${BASE}/catalog/parts`, "POST"),
        invalidatesTags: [
          { type: "FactoryPart", id: "FULL" },
          { type: "FactoryPart", id: "LIST" },
        ],
      }),

      updateFactoryPart: builder.mutation({
        query: ({ token, id, ...body }) =>
          apiFunction({ token, ...body }, `${BASE}/catalog/parts/${id}`, "PATCH"),
        invalidatesTags: [
          { type: "FactoryPart", id: "FULL" },
          { type: "FactoryPart", id: "LIST" },
        ],
      }),

      deleteFactoryPart: builder.mutation({
        query: ({ token, id }) =>
          apiFunction({ token }, `${BASE}/catalog/parts/${id}`, "DELETE"),
        invalidatesTags: [
          { type: "FactoryPart", id: "FULL" },
          { type: "FactoryPart", id: "LIST" },
        ],
      }),

      createFactoryMachineType: builder.mutation({
        query: ({ token, ...body }) =>
          apiFunction({ token, ...body }, `${BASE}/catalog/machine-types`, "POST"),
        invalidatesTags: [{ type: "FactoryPart", id: "FULL" }],
      }),

      updateFactoryMachineType: builder.mutation({
        query: ({ token, id, ...body }) =>
          apiFunction(
            { token, ...body },
            `${BASE}/catalog/machine-types/${id}`,
            "PATCH"
          ),
        invalidatesTags: [{ type: "FactoryPart", id: "FULL" }],
      }),

      updateFactoryBom: builder.mutation({
        query: ({ token, id, slots }) =>
          apiFunction(
            { token, slots },
            `${BASE}/catalog/machine-types/${id}/bom`,
            "PUT"
          ),
        invalidatesTags: [{ type: "FactoryPart", id: "FULL" }],
      }),

      // ── Katalog görselleri ─────────────────────────────────────────────
      uploadFactoryPartImage: builder.mutation({
        query: ({ token, id, file }) =>
          imageUpload(token, `${BASE}/catalog/parts/${id}/image`, file),
        invalidatesTags: [
          { type: "FactoryPart", id: "FULL" },
          { type: "FactoryPart", id: "LIST" },
          { type: "FactoryPart", id: "CATALOG" },
        ],
      }),

      deleteFactoryPartImage: builder.mutation({
        query: ({ token, id }) =>
          apiFunction({ token }, `${BASE}/catalog/parts/${id}/image`, "DELETE"),
        invalidatesTags: [
          { type: "FactoryPart", id: "FULL" },
          { type: "FactoryPart", id: "LIST" },
          { type: "FactoryPart", id: "CATALOG" },
        ],
      }),

      uploadFactoryMachineTypeImage: builder.mutation({
        query: ({ token, id, file }) =>
          imageUpload(token, `${BASE}/catalog/machine-types/${id}/image`, file),
        invalidatesTags: [
          { type: "FactoryPart", id: "FULL" },
          { type: "FactoryPart", id: "CATALOG" },
        ],
      }),

      deleteFactoryMachineTypeImage: builder.mutation({
        query: ({ token, id }) =>
          apiFunction(
            { token },
            `${BASE}/catalog/machine-types/${id}/image`,
            "DELETE"
          ),
        invalidatesTags: [
          { type: "FactoryPart", id: "FULL" },
          { type: "FactoryPart", id: "CATALOG" },
        ],
      }),

      // ── Bölgeler & tabletler ───────────────────────────────────────────
      getFactoryZones: builder.query({
        query: ({ token }) => authGet(token, `${BASE}/zones`),
        transformResponse: (r) => r?.data?.items || [],
        providesTags: [{ type: "FactoryZone", id: "LIST" }],
      }),

      createFactoryZone: builder.mutation({
        query: ({ token, ...body }) => apiFunction({ token, ...body }, `${BASE}/zones`, "POST"),
        invalidatesTags: [{ type: "FactoryZone", id: "LIST" }],
      }),

      updateFactoryZone: builder.mutation({
        query: ({ token, id, ...body }) =>
          apiFunction({ token, ...body }, `${BASE}/zones/${id}`, "PATCH"),
        invalidatesTags: [{ type: "FactoryZone", id: "LIST" }],
      }),

      deleteFactoryZone: builder.mutation({
        query: ({ token, id }) => apiFunction({ token }, `${BASE}/zones/${id}`, "DELETE"),
        invalidatesTags: [{ type: "FactoryZone", id: "LIST" }],
      }),

      // Kroki yapı öğeleri: duvar / kapı / zemin — D-062
      getFactoryLayoutItems: builder.query({
        query: ({ token }) => authGet(token, `${BASE}/layout-items`),
        transformResponse: (r) => r?.data?.items || [],
        providesTags: [{ type: "FactoryZone", id: "LAYOUT" }],
      }),

      createFactoryLayoutItem: builder.mutation({
        query: ({ token, ...body }) =>
          apiFunction({ token, ...body }, `${BASE}/layout-items`, "POST"),
        invalidatesTags: [{ type: "FactoryZone", id: "LAYOUT" }],
      }),

      updateFactoryLayoutItem: builder.mutation({
        query: ({ token, id, ...body }) =>
          apiFunction({ token, ...body }, `${BASE}/layout-items/${id}`, "PATCH"),
        invalidatesTags: [{ type: "FactoryZone", id: "LAYOUT" }],
      }),

      deleteFactoryLayoutItem: builder.mutation({
        query: ({ token, id }) =>
          apiFunction({ token }, `${BASE}/layout-items/${id}`, "DELETE"),
        invalidatesTags: [{ type: "FactoryZone", id: "LAYOUT" }],
      }),

      getFactoryKiosks: builder.query({
        query: ({ token }) => authGet(token, `${BASE}/kiosks`),
        transformResponse: (r) => r?.data?.items || [],
        providesTags: [{ type: "FactoryZone", id: "KIOSKS" }],
      }),

      // ── Programlama İşlemleri ──────────────────────────────────────────
      // Okkalı Servis panelindeki Programlama Görevlisi hesaplarının faaliyet
      // logu. Kaynağı audit koleksiyonu, ama uç fabrika altında —
      // yetki zinciri diğer fabrika uçlarıyla aynı (factory.overview.read).
      // Contract: BayinerIOT-Workspace-/docs/api-contracts/panel-activity.md
      //
      // Sunucu sayfalama yapıyor: yanıt { items, total, page, limit }.
      // transformResponse ile items'a İNDİRGENMEZ — sayfalama için total lazım.
      // ── Belgeler (D-071) ───────────────────────────────────────────────
      // Tablette kart okutan çalışanın okuyacağı PDF'ler. Yükleme akışı
      // Hardware Update ile aynı: presign → doğrudan depolamaya PUT → create.
      getFactoryDocumentGroups: builder.query({
        query: ({ token }) => authGet(token, `${BASE}/documents/groups`),
        transformResponse: (r) => r?.data?.groups || [],
        providesTags: [{ type: "FactoryDocument", id: "GROUPS" }],
      }),

      getFactoryDocuments: builder.query({
        query: ({ token, ...params }) =>
          authGet(token, `${BASE}/documents${buildQs(params)}`),
        transformResponse: (r) => r?.data?.documents || [],
        providesTags: [{ type: "FactoryDocument", id: "LIST" }],
      }),

      presignFactoryDocument: builder.mutation({
        query: ({ token, fileName, mimeType }) =>
          apiFunction(
            { token, fileName, mimeType },
            `${BASE}/documents/upload/presign`,
            "POST",
          ),
      }),

      createFactoryDocument: builder.mutation({
        query: ({ token, ...body }) =>
          apiFunction({ token, ...body }, `${BASE}/documents`, "POST"),
        invalidatesTags: [{ type: "FactoryDocument", id: "LIST" }],
      }),

      updateFactoryDocument: builder.mutation({
        query: ({ token, id, ...body }) =>
          apiFunction({ token, ...body }, `${BASE}/documents/${id}`, "PATCH"),
        invalidatesTags: [{ type: "FactoryDocument", id: "LIST" }],
      }),

      deleteFactoryDocument: builder.mutation({
        query: ({ token, id }) =>
          apiFunction({ token }, `${BASE}/documents/${id}`, "DELETE"),
        invalidatesTags: [{ type: "FactoryDocument", id: "LIST" }],
      }),

      getPanelActivity: builder.query({
        query: ({ token, ...params }) =>
          authGet(token, `${BASE}/panel-activity${buildQs(params)}`),
        transformResponse: (r) => r?.data || { items: [], total: 0 },
        providesTags: [{ type: "PanelActivity", id: "LIST" }],
      }),
    };
  },
});

export const {
  useGetFactoryFullCatalogQuery,
  useCreateFactoryLabelMutation,
  useUpdateFactoryLabelMutation,
  useDeleteFactoryLabelMutation,
  useDeleteFactoryPartMutation,
  useCreateFactoryPartMutation,
  useUpdateFactoryPartMutation,
  useCreateFactoryMachineTypeMutation,
  useUpdateFactoryMachineTypeMutation,
  useUpdateFactoryBomMutation,
  useUploadFactoryPartImageMutation,
  useDeleteFactoryPartImageMutation,
  useUploadFactoryMachineTypeImageMutation,
  useDeleteFactoryMachineTypeImageMutation,
  useGetFactoryOverviewQuery,
  useGetFactoryOperationsQuery,
  useCancelFactoryOperationLateMutation,
  useGetFactoryPartsQuery,
  useGetFactoryPartUnitsQuery,
  useGetFactoryPartUnitDetailQuery,
  useDeleteFactoryPartUnitMutation,
  useGetFactoryMachinesQuery,
  useGetFactoryBuildsQuery,
  useGetFactoryCatalogQuery,
  useGetFactoryZonesQuery,
  useCreateFactoryZoneMutation,
  useUpdateFactoryZoneMutation,
  useDeleteFactoryZoneMutation,
  useGetFactoryKiosksQuery,
  useGetFactoryLayoutItemsQuery,
  useCreateFactoryLayoutItemMutation,
  useUpdateFactoryLayoutItemMutation,
  useDeleteFactoryLayoutItemMutation,
  useGetPanelActivityQuery,
  useGetFactoryDocumentGroupsQuery,
  useGetFactoryDocumentsQuery,
  usePresignFactoryDocumentMutation,
  useCreateFactoryDocumentMutation,
  useUpdateFactoryDocumentMutation,
  useDeleteFactoryDocumentMutation,
} = factoryApi;
